import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardList, Plus, RefreshCcw, Inbox, Users, Calendar, Clock,
  User as UserIcon, Target, AlertCircle, Sparkles, Pencil, AlertTriangle,
} from "lucide-react";
import { moduleCanDo, tabAccessibleFor } from "../lib/modules";
import { useGlobalViewMode } from "../components/ViewModeToggle";

const MONTHS = [
  { v: 1,  label: "January" },   { v: 2,  label: "February" },
  { v: 3,  label: "March" },     { v: 4,  label: "April" },
  { v: 5,  label: "May" },       { v: 6,  label: "June" },
  { v: 7,  label: "July" },      { v: 8,  label: "August" },
  { v: 9,  label: "September" }, { v: 10, label: "October" },
  { v: 11, label: "November" },  { v: 12, label: "December" },
];

function currentYear() { return new Date().getFullYear(); }
function currentMonth() { return new Date().getMonth() + 1; }
function yearOptions() {
  const y = currentYear();
  return [y - 2, y - 1, y, y + 1];
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
}
function fmtTime(t) {
  if (!t) return "—";
  // Accept "HH:MM" or full ISO / date-time strings.
  if (/^\d{1,2}:\d{2}$/.test(String(t))) {
    const [h, m] = String(t).split(":").map(Number);
    const suffix = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${suffix}`;
  }
  try {
    return new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return t; }
}
function fmtDateTime(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return d; }
}
function todayIso() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
/** v109.9.126 — yesterday in local timezone, ISO YYYY-MM-DD. */
function yesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// ────────────────────────────────────────────────────────────────────
// Main
// ────────────────────────────────────────────────────────────────────
export default function DailyReport({ actionPulse, embedded } = {}) {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const canManage = isMaster || tabAccessibleFor(user, "hr_daily_report", "all") || moduleCanDo(user, "hr_daily_report", "edit");

  const [tab, setTab] = useState("mine");
  const [viewMode] = useGlobalViewMode();

  // Filters
  const [monthMine, setMonthMine] = useState(currentMonth());
  const [yearMine, setYearMine]   = useState(currentYear());
  const [monthAll, setMonthAll]   = useState(currentMonth());
  const [yearAll, setYearAll]     = useState(currentYear());
  const [userAll, setUserAll]     = useState("all"); // "all" | user_id

  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  // v109.9.126 — Late-report auto-open. When the backend says the user has
  // an unfiled past day (they punched in but never filed the report), we
  // pop the "New Report" dialog with the missing date pre-filled and lock
  // the picker to previous dates only.
  const [pendingLate, setPendingLate] = useState({ auto_open: false, unfiled_dates: [], next_unfiled: null, checked: false });
  const [initialReportDate, setInitialReportDate] = useState(null);

  const loadRows = useCallback(async () => {
    try {
      const { data } = await api.get("/records/hr_daily_report");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not load reports");
    }
  }, []);

  const loadUsers = useCallback(async () => {
    if (!canManage) return;
    try {
      const { data } = await api.get("/employees");
      setUsers(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [canManage]);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // v109.9.126 — On mount, ask the backend whether the user missed a
  // previous day's report. If so, auto-open the New Report dialog with
  // that date pre-filled.
  useEffect(() => {
    let cancelled = false;
    api.get("/daily-report/pending-late")
      .then(({ data }) => {
        if (cancelled) return;
        setPendingLate({ ...data, checked: true });
        if (data?.auto_open && data?.next_unfiled) {
          setInitialReportDate(data.next_unfiled);
          setOpenNew(true);
        }
      })
      .catch(() => { if (!cancelled) setPendingLate((s) => ({ ...s, checked: true })); });
    return () => { cancelled = true; };
  }, []);

  // Deep-link / dashboard pulse to open the "new report" dialog.
  useEffect(() => {
    if (actionPulse?.key === "new") setOpenNew(true);
  }, [actionPulse?.key, actionPulse?.n]);

  // v109.9.148 — Deep link from My Attendance:
  //   /daily-report?new=YYYY-MM-DD  → open the New Report dialog with the
  //   requested date pre-filled (user can still change it inside the form).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const newParam = searchParams.get("new");
    if (newParam) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(newParam)) {
        setInitialReportDate(newParam);
      }
      setOpenNew(true);
      // Clean up the URL so refresh doesn't re-open.
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inPeriod = (r, y, m) => {
    const d = r.report_date ? new Date(r.report_date) : null;
    if (!d || isNaN(d)) return false;
    return d.getFullYear() === Number(y) && d.getMonth() + 1 === Number(m);
  };

  const mineRows = useMemo(() => {
    return rows
      .filter((r) => r.created_by_id === user?.id)
      .filter((r) => inPeriod(r, yearMine, monthMine))
      .sort((a, b) => (b.report_date || "").localeCompare(a.report_date || ""));
  }, [rows, user?.id, yearMine, monthMine]);

  const allRows = useMemo(() => {
    return rows
      .filter((r) => (userAll === "all" ? true : r.created_by_id === userAll))
      .filter((r) => inPeriod(r, yearAll, monthAll))
      .sort((a, b) => (b.report_date || "").localeCompare(a.report_date || ""));
  }, [rows, userAll, yearAll, monthAll]);

  const openEdit = (r) => setEditing(r);
  const closeEdit = () => setEditing(null);

  return (
    <div className="max-w-[1400px] space-y-6">
      <PageHeader
        moduleKey="hr_daily_report"
        tone="emerald"
        icon={ClipboardList}
        eyebrow="HR Module"
        title="Daily Report"
        subtitle="See your submissions, review the team's reports, and keep your daily log up to date."
      />

      {/* v109.9.126 — Late-report banner */}
      {pendingLate.unfiled_dates?.length > 0 && (
        <div
          className="rounded-xl border-l-4 border border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 p-3 md:p-4 flex items-start gap-3"
          data-testid="dailyreport-late-banner"
        >
          <AlertTriangle size={18} className="mt-0.5 text-amber-600 shrink-0" strokeWidth={2.3} />
          <div className="flex-1">
            <div className="text-[13px] font-bold text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
              You have {pendingLate.unfiled_dates.length} pending late report{pendingLate.unfiled_dates.length > 1 ? "s" : ""}
              <Badge variant="secondary" className="ml-1 bg-amber-200/80 dark:bg-amber-900/60 text-amber-900 dark:text-amber-100 text-[10.5px]">Late Report</Badge>
            </div>
            <div className="text-[12px] text-amber-700 dark:text-amber-300 mt-0.5">
              Missing: <span className="font-mono">{pendingLate.unfiled_dates.map(fmtDate).join(", ")}</span>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => { setInitialReportDate(pendingLate.next_unfiled); setOpenNew(true); }}
            className="h-8 bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="dailyreport-late-file-btn"
          >
            <Plus size={13} className="mr-1" /> File Now
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1 bg-slate-100 dark:bg-slate-900/60 p-1 rounded-lg" data-testid="dailyreport-tabs">
          <TabsTrigger value="mine" data-testid="dailyreport-tab-mine" className="flex items-center gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Inbox className="w-3.5 h-3.5" /> My Submission
          </TabsTrigger>
          {canManage && (
            <TabsTrigger value="all" data-testid="dailyreport-tab-all" className="flex items-center gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Users className="w-3.5 h-3.5" /> All
            </TabsTrigger>
          )}
        </TabsList>

        {/* My Submission */}
        <TabsContent value="mine" className="mt-4 space-y-3" data-testid="dailyreport-mine-content">
          <FilterBar
            year={yearMine} setYear={setYearMine}
            month={monthMine} setMonth={setMonthMine}
            onRefresh={loadRows}
            onNew={() => setOpenNew(true)}
            count={mineRows.length}
            testidPrefix="dailyreport-mine"
          />
          <ReportsView
            mode={viewMode}
            rows={mineRows}
            showEmployee={false}
            onEdit={openEdit}
            emptyMsg={`No reports submitted for ${MONTHS[monthMine - 1].label} ${yearMine} yet.`}
            testidPrefix="dailyreport-mine"
          />
        </TabsContent>

        {/* All */}
        {canManage && (
          <TabsContent value="all" className="mt-4 space-y-3" data-testid="dailyreport-all-content">
            <FilterBar
              year={yearAll} setYear={setYearAll}
              month={monthAll} setMonth={setMonthAll}
              users={users}
              userValue={userAll} setUserValue={setUserAll}
              onRefresh={loadRows}
              onNew={() => setOpenNew(true)}
              count={allRows.length}
              testidPrefix="dailyreport-all"
            />
            <ReportsView
              mode={viewMode}
              rows={allRows}
              showEmployee={true}
              onEdit={openEdit}
              emptyMsg={`No reports found for ${MONTHS[monthAll - 1].label} ${yearAll}${userAll !== "all" ? " · selected user" : ""}.`}
              testidPrefix="dailyreport-all"
            />
          </TabsContent>
        )}
      </Tabs>

      {openNew && (
        <ReportDialog
          open={openNew}
          initial={null}
          initialReportDate={initialReportDate}
          hasPendingLate={pendingLate.unfiled_dates?.length > 0}
          onClose={() => { setOpenNew(false); setInitialReportDate(null); }}
          onSaved={() => {
            setOpenNew(false); setInitialReportDate(null); loadRows();
            // Refresh late-pending status — the file we just submitted
            // may have cleared the earliest missing date.
            api.get("/daily-report/pending-late")
              .then(({ data }) => setPendingLate({ ...data, checked: true }))
              .catch(() => {});
          }}
        />
      )}
      {editing && (
        <ReportDialog
          open={!!editing}
          initial={editing}
          onClose={closeEdit}
          onSaved={() => { closeEdit(); loadRows(); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Filter bar (year + month + optional user + refresh + new)
// ────────────────────────────────────────────────────────────────────
function FilterBar({ year, setYear, month, setMonth, users, userValue, setUserValue, onRefresh, onNew, count, testidPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1.5">
        <Calendar size={14} className="text-slate-500" />
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="h-9 w-36" data-testid={`${testidPrefix}-month`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-9 w-24" data-testid={`${testidPrefix}-year`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {yearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {users && (
        <div className="flex items-center gap-1.5">
          <UserIcon size={14} className="text-slate-500" />
          <Select value={userValue} onValueChange={setUserValue}>
            <SelectTrigger className="h-9 w-56" data-testid={`${testidPrefix}-user`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} {u.employee_code ? `· ${u.employee_code}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex-1" />
      <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">{count} record{count !== 1 ? "s" : ""}</Badge>
      <Button size="sm" variant="outline" onClick={onRefresh} className="h-9" data-testid={`${testidPrefix}-refresh`}>
        <RefreshCcw size={14} className="mr-1.5" /> Refresh
      </Button>
      <Button size="sm" onClick={onNew} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`${testidPrefix}-new`}>
        <Plus size={14} className="mr-1.5" /> New Report
      </Button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Reports rendering — card vs list
// ────────────────────────────────────────────────────────────────────
function ReportsView({ mode, rows, showEmployee, onEdit, emptyMsg, testidPrefix }) {
  if (rows.length === 0) {
    return <div className="pk-card p-10 text-center text-slate-400 text-sm" data-testid={`${testidPrefix}-empty`}>{emptyMsg}</div>;
  }
  if (mode === "list") return <ReportsList rows={rows} showEmployee={showEmployee} onEdit={onEdit} testidPrefix={testidPrefix} />;
  return <ReportsCards rows={rows} showEmployee={showEmployee} onEdit={onEdit} testidPrefix={testidPrefix} />;
}

function ReportsCards({ rows, showEmployee, onEdit, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {rows.map((r) => (
        <div key={r.id} className="relative" data-testid={`${testidPrefix}-card-wrap-${r.id}`}>
          {/* v109.9.126 — Late Report tag lives OUTSIDE the card as
              requested, floating above the top-right corner. */}
          {r.is_late && (
            <Badge
              className="absolute -top-2 -right-2 z-10 bg-amber-500 text-white border-amber-600 flex items-center gap-1 shadow-md"
              data-testid={`${testidPrefix}-late-tag-${r.id}`}
              title="Filed after the report date"
            >
              <AlertTriangle size={10} /> Late Report
            </Badge>
          )}
          <div className={`pk-card p-4 space-y-2 hover:shadow-md transition-shadow ${r.is_late ? "ring-1 ring-amber-300 dark:ring-amber-700/60" : ""}`} data-testid={`${testidPrefix}-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-1.5 text-[12.5px] text-slate-500 dark:text-slate-400">
                  <Calendar size={12} /> {fmtDate(r.report_date)}
                  <span className="text-slate-300">·</span>
                  <Clock size={12} /> {fmtTime(r.report_time)}
                  {r.is_late && (
                    <span className="text-amber-600 dark:text-amber-400 inline-flex items-center gap-0.5 text-[11px] font-semibold">
                      · <AlertTriangle size={10} /> Late
                    </span>
                  )}
                </div>
                {showEmployee && (
                  <div className="mt-0.5 flex items-center gap-1 text-[13px] font-semibold text-slate-900 dark:text-white">
                    <UserIcon size={12} className="text-slate-500" /> {r.employee_name || r.created_by_name || "—"}
                  </div>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={() => onEdit(r)} className="h-7 w-7 p-0" data-testid={`${testidPrefix}-edit-${r.id}`}>
                <Pencil size={13} />
              </Button>
            </div>
            <ReportField icon={Target} tone="emerald" label="Tasks Done" text={r.tasks_completed} />
            {r.challenges && <ReportField icon={AlertCircle} tone="amber" label="Challenges" text={r.challenges} />}
            {r.next_day_plan && <ReportField icon={Sparkles} tone="sky" label="Plan Tomorrow" text={r.next_day_plan} />}
            <div className="text-[10.5px] text-slate-400 dark:text-slate-500 mono pt-1 border-t border-slate-100 dark:border-slate-800">
              Submitted {fmtDateTime(r.submitted_at_ist || r.created_at || r.report_date)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportField({ icon: Icon, tone, label, text }) {
  const toneMap = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber:   "text-amber-600 dark:text-amber-400",
    sky:     "text-sky-600 dark:text-sky-400",
  };
  return (
    <div>
      <div className={`flex items-center gap-1 text-[10.5px] uppercase tracking-wider font-bold ${toneMap[tone] || toneMap.emerald}`}>
        <Icon size={11} /> {label}
      </div>
      <div className="text-[12.5px] text-slate-700 dark:text-slate-300 mt-0.5 whitespace-pre-wrap line-clamp-3">{text}</div>
    </div>
  );
}

function ReportsList({ rows, showEmployee, onEdit, testidPrefix }) {
  return (
    <div className="pk-card overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 bg-slate-50/60 dark:bg-slate-900/40">
          <tr>
            <th className="px-4 py-3 text-left pk-table-header">Date</th>
            <th className="px-4 py-3 text-left pk-table-header">Time</th>
            {showEmployee && <th className="px-4 py-3 text-left pk-table-header">Employee</th>}
            <th className="px-4 py-3 text-left pk-table-header">Tasks Completed</th>
            <th className="px-4 py-3 text-left pk-table-header">Challenges</th>
            <th className="px-4 py-3 text-left pk-table-header">Plan Tomorrow</th>
            <th className="px-4 py-3 text-right pk-table-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-900/40" data-testid={`${testidPrefix}-row-${r.id}`}>
              <td className="px-4 py-3 text-[12.5px] text-slate-800 dark:text-slate-200" data-label="Date">
                <div className="flex items-center gap-1.5">
                  {fmtDate(r.report_date)}
                  {r.is_late && (
                    <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-800 text-[10px] px-1.5 py-0" data-testid={`${testidPrefix}-late-tag-${r.id}`}>
                      <AlertTriangle size={9} className="mr-0.5" /> Late
                    </Badge>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-[12.5px] mono text-slate-600 dark:text-slate-300" data-label="Time">{fmtTime(r.report_time)}</td>
              {showEmployee && (
                <td className="px-4 py-3" data-label="Employee">
                  <div className="text-[13px] font-semibold text-slate-900 dark:text-white">{r.employee_name || r.created_by_name || "—"}</div>
                </td>
              )}
              <td className="px-4 py-3 text-[12.5px] text-slate-700 dark:text-slate-300 max-w-md" data-label="Tasks Completed">
                <div className="line-clamp-2 whitespace-pre-wrap">{r.tasks_completed || "—"}</div>
              </td>
              <td className="px-4 py-3 text-[12.5px] text-slate-600 dark:text-slate-300 max-w-xs" data-label="Challenges">
                <div className="line-clamp-2 whitespace-pre-wrap">{r.challenges || "—"}</div>
              </td>
              <td className="px-4 py-3 text-[12.5px] text-slate-600 dark:text-slate-300 max-w-xs" data-label="Plan Tomorrow">
                <div className="line-clamp-2 whitespace-pre-wrap">{r.next_day_plan || "—"}</div>
              </td>
              <td className="px-4 py-3 text-right" data-label="Actions">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onEdit(r)} data-testid={`${testidPrefix}-list-edit-${r.id}`}>
                  <Pencil size={12} className="mr-1" /> View
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// New / View dialog
// ────────────────────────────────────────────────────────────────────
function ReportDialog({ open, initial, onClose, onSaved, initialReportDate = null, hasPendingLate = false }) {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const isEdit = !!initial;
  const isOwn = !isEdit || (initial?.created_by_id === user?.id);
  // v109.9.153 — Daily Report is IMMUTABLE once submitted. Nobody (not even
  // Master Admin) can edit CONTENT — only status changes (Approve/Reject).
  // Draft and Rejected reports remain editable by the owner (Rejected → resubmit).
  const status = (initial?.status || "").trim();
  const isSubmittedState = ["Submitted", "Approved", "In Review", "Pending"].includes(status);
  const canEdit = isOwn && (!isEdit || !isSubmittedState);

  // v109.9.126 — When the dialog opens from the late-report auto-flow, we
  // pre-fill `report_date` with the missing day (yesterday or earlier).
  // In "new" mode we now default to `yesterday` when a pending-late exists
  // and to `today` otherwise. The picker's `max` is always today so future
  // dates cannot be chosen.
  const defaultDate = initial?.report_date
    || initialReportDate
    || (hasPendingLate ? yesterdayIso() : todayIso());
  const [form, setForm] = useState({
    report_date:      defaultDate,
    report_time:      initial?.report_time || nowTime(),
    tasks_completed:  initial?.tasks_completed || "",
    challenges:       initial?.challenges || "",
    next_day_plan:    initial?.next_day_plan || "",
  });
  const [saving, setSaving] = useState(false);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if ((form.tasks_completed || "").trim().length < 3) {
      toast.error("Please add tasks completed today (min 3 characters)."); return;
    }
    // v109.9.126 — Guard: on new reports, only past or today allowed. On
    // late-report auto-flow the max is yesterday so the user cannot bypass
    // the requirement by picking today.
    if (!isEdit) {
      const maxAllowed = hasPendingLate ? yesterdayIso() : todayIso();
      if (form.report_date > maxAllowed) {
        toast.error(hasPendingLate
          ? "Late report must be for a previous date (yesterday or earlier)."
          : "Future dates are not allowed."
        );
        return;
      }
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/records/hr_daily_report/${initial.id}`, form);
        toast.success("Report updated");
      } else {
        await api.post("/records/hr_daily_report", form);
        toast.success("Report submitted");
      }
      onSaved();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="dailyreport-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <ClipboardList size={18} /> {isEdit ? "Daily Report" : "New Daily Report"}
            {/* v109.9.126 — Late-Report badge at the top of the dialog. */}
            {((isEdit && initial?.is_late) || (!isEdit && form.report_date && form.report_date < todayIso())) && (
              <Badge className="ml-1 bg-amber-500 text-white border-amber-600 flex items-center gap-1" data-testid="dailyreport-late-tag-dialog">
                <AlertTriangle size={11} /> Late Report
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Submitted by ${initial?.employee_name || initial?.created_by_name || "—"} · ${fmtDateTime(initial?.submitted_at_ist || initial?.created_at || initial?.report_date)}`
              : hasPendingLate
                ? "Filing a late report for a previous day. Only past dates are allowed."
                : "Log today's work — tasks, challenges and plan for tomorrow."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Date</Label>
              <Input
                type="date"
                value={form.report_date}
                onChange={(e) => setF("report_date", e.target.value)}
                disabled={!canEdit}
                max={isEdit ? undefined : (hasPendingLate ? yesterdayIso() : todayIso())}
                className="mt-1.5 h-10 mono"
                data-testid="dailyreport-date"
              />
              {!isEdit && hasPendingLate && (
                <div className="text-[10.5px] text-amber-600 dark:text-amber-400 mt-0.5">Late-report mode · picker locked to yesterday or earlier.</div>
              )}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Time</Label>
              <Input type="time" value={form.report_time} onChange={(e) => setF("report_time", e.target.value)} disabled={!canEdit} className="mt-1.5 h-10 mono" data-testid="dailyreport-time" />
              <div className="text-[10.5px] text-slate-500 mt-0.5">Shown as {fmtTime(form.report_time)}</div>
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1"><Target size={11} className="text-emerald-600" /> Tasks Completed <span className="text-rose-500">*</span></Label>
            <Textarea value={form.tasks_completed} onChange={(e) => setF("tasks_completed", e.target.value)} disabled={!canEdit} rows={3} placeholder="What did you accomplish today?" className="mt-1.5" data-testid="dailyreport-tasks" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1"><AlertCircle size={11} className="text-amber-600" /> Challenges</Label>
            <Textarea value={form.challenges} onChange={(e) => setF("challenges", e.target.value)} disabled={!canEdit} rows={2} placeholder="Anything blocking you?" className="mt-1.5" data-testid="dailyreport-challenges" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1"><Sparkles size={11} className="text-sky-600" /> Plan for Tomorrow</Label>
            <Textarea value={form.next_day_plan} onChange={(e) => setF("next_day_plan", e.target.value)} disabled={!canEdit} rows={2} placeholder="What will you tackle tomorrow?" className="mt-1.5" data-testid="dailyreport-plan" />
          </div>

          {isEdit && !canEdit && (
            <div className="text-[11.5px] text-slate-500 italic">This report is view-only.</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{canEdit ? "Cancel" : "Close"}</Button>
          {canEdit && (
            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dailyreport-submit">
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Submit Report"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
